import {
  IconButton, useTheme, TextField, Container, Paper,
} from "@mui/material";
import { tokens } from "../../theme";
import { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from "react-redux";
import RefreshIcon from '@mui/icons-material/Refresh';
import { __fetching_data__ } from '../../components/api';
import { createChart, PriceScaleMode } from 'lightweight-charts';
import { useIsMobile } from '../../components/hooks';
import Autocomplete from '@mui/material/Autocomplete';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import FastForwardIcon from '@mui/icons-material/FastForward';

// ── Module-level vars ─────────────────────────────────────────────────────────
var globalResponse = null;
var liveData       = null;
var liveCharts     = {};   // { [key]: { chart, seriesRef, _seriesArray?, renderMarkers? } }
var liveSymbol     = '';

const viewportHeight = window.innerHeight;
const chartHeight    = (viewportHeight - 50 - 45) / 3;

// Chart keys in display order
const CHART_KEYS = ['Last', '%Short', 'PredictTrend', 'Peak', 'Shareholder', 'manager'];

// ====================================================
// Toast
// ====================================================
function ToastContainer({ toasts }) {
  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[9999] flex flex-col items-center gap-2 pointer-events-none">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`flex items-center gap-3 px-5 py-3 rounded-xl shadow-2xl border text-sm font-medium
            backdrop-blur-md transition-all duration-300 relative overflow-hidden
            ${t.type === "success"
              ? "bg-[#0a1628]/90 border-cyan-500/40 text-cyan-200"
              : t.type === "error"
                ? "bg-[#0a1628]/90 border-red-500/40 text-red-300"
                : "bg-[#0a1628]/90 border-slate-500/40 text-slate-300"}`}
          style={{ animation: "toastIn 0.3s cubic-bezier(0.34,1.56,0.64,1) forwards" }}
        >
          {t.type === "success" && <CheckCircleOutlineIcon fontSize="small" className="text-cyan-400 flex-shrink-0" />}
          {t.type === "error"   && <ErrorOutlineIcon      fontSize="small" className="text-red-400 flex-shrink-0" />}
          <span>{t.message}</span>
          <div
            className={`absolute bottom-0 left-0 h-[2px] rounded-b-xl
              ${t.type === "success" ? "bg-cyan-500" : t.type === "error" ? "bg-red-500" : "bg-slate-500"}`}
            style={{ animation: `toastProgress ${t.duration}ms linear forwards`, width: "100%" }}
          />
        </div>
      ))}
      <style>{`
        @keyframes toastIn       { from{opacity:0;transform:translateY(12px) scale(.95)} to{opacity:1;transform:translateY(0) scale(1)} }
        @keyframes toastProgress { from{width:100%} to{width:0%} }
      `}</style>
    </div>
  );
}

function useToast() {
  const [toasts, setToasts] = useState([]);
  const showToast = (message, type = "success", duration = 3000) => {
    const id = Date.now() + Math.random();
    setToasts(p => [...p, { id, message, type, duration }]);
    setTimeout(() => setToasts(p => p.filter(t => t.id !== id)), duration);
  };
  return { toasts, showToast };
}

// ====================================================
// Per-chart skeleton overlay (shown while API loads)
// ====================================================
function ChartSkeleton() {
  return (
    <div
      style={{
        position: 'absolute', inset: 0, zIndex: 500,
        background: 'rgba(11,14,20,0.82)',
        backdropFilter: 'blur(2px)',
        borderRadius: '8px',
        pointerEvents: 'none',
        display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
        padding: '12px',
      }}
    >
      <style>{`@keyframes skshimmer{0%{transform:translateX(-100%)}100%{transform:translateX(100%)}}`}</style>

      {/* top label bars */}
      <div style={{ display:'flex', gap:'8px' }}>
        <div style={{ height:8, borderRadius:4, background:'#1e293b', width:'30%' }} />
        <div style={{ height:8, borderRadius:4, background:'#1e293b', width:'18%' }} />
      </div>

      {/* fake chart body */}
      <div style={{ flex:1, margin:'10px 0', position:'relative', overflow:'hidden', borderRadius:4 }}>
        <div style={{ position:'absolute', inset:0, background:'rgba(30,41,59,0.6)', borderRadius:4 }} />
        <svg style={{ position:'absolute', inset:0, width:'100%', height:'100%' }} preserveAspectRatio="none">
          <polyline
            points="0,75 80,55 160,68 240,38 320,52 400,28 480,44 560,33 640,48 720,36 800,20 880,35"
            fill="none" stroke="rgba(56,189,248,0.20)" strokeWidth="2"
          />
        </svg>
        {/* shimmer sweep */}
        <div style={{
          position:'absolute', inset:0,
          background:'linear-gradient(90deg,transparent 0%,rgba(56,189,248,0.12) 40%,rgba(125,211,252,0.25) 50%,rgba(56,189,248,0.12) 60%,transparent 100%)',
          animation:'skshimmer 1.6s ease-in-out infinite',
        }} />
      </div>

      {/* bottom axis ticks */}
      <div style={{ display:'flex', gap:'6px' }}>
        {[...Array(6)].map((_,i) => (
          <div key={i} style={{ height:6, borderRadius:3, background:'#1e293b', flex:1 }} />
        ))}
      </div>
    </div>
  );
}

// ====================================================
// Full-grid wave skeleton (first page load)
// ====================================================
function WaveSkeleton({ delay = 0 }) {
  return (
    <div style={{ width:'100%', height:chartHeight, background:'#0f172a', borderRadius:'8px', overflow:'hidden', position:'relative' }}>
      <style>{`@keyframes wvshimmer{0%{transform:translateX(-100%)}100%{transform:translateX(100%)}}`}</style>
      <div style={{ position:'absolute', inset:0, display:'flex', flexDirection:'column', justifyContent:'space-between', padding:'12px' }}>
        <div style={{ display:'flex', gap:'8px' }}>
          <div style={{ height:8, borderRadius:4, background:'#1e293b', width:'33%' }} />
          <div style={{ height:8, borderRadius:4, background:'#1e293b', width:'20%' }} />
        </div>
        <div style={{ flex:1, margin:'12px 0', borderRadius:4, background:'rgba(30,41,59,0.6)' }} />
        <div style={{ display:'flex', gap:'10px' }}>
          {[...Array(5)].map((_,i)=><div key={i} style={{ height:8, borderRadius:4, background:'#1e293b', flex:1 }} />)}
        </div>
      </div>
      <div style={{
        position:'absolute', inset:0,
        background:'linear-gradient(90deg,transparent 0%,rgba(56,189,248,0.08) 40%,rgba(125,211,252,0.18) 50%,rgba(56,189,248,0.08) 60%,transparent 100%)',
        animation:`wvshimmer 1.8s ease-in-out ${delay}s infinite`,
      }} />
    </div>
  );
}

// ====================================================
// MAIN COMPONENT
// ====================================================
const ChatBotV2Page = () => {
  const theme    = useTheme();
  const isMobile = useIsMobile();
  const { toasts, showToast } = useToast();

  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isSymbolSelected,  setIsSymbolSelected]  = useState(false);
  const [loading,    setLoading]    = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [messages,   setMessages]   = useState([]);
  const [isMessageSent, setIsMessageSent] = useState(false);

  // ── per-chart loading flags ───────────────────────────────────────────────
  const [chartLoading, setChartLoading] = useState(
    () => Object.fromEntries(CHART_KEYS.map(k => [k, false]))
  );
  const markLoading = useCallback((keys, val) =>
    setChartLoading(prev => {
      const next = { ...prev };
      keys.forEach(k => { next[k] = val; });
      return next;
    }), []);

  let [ddlChartOrder, setDDLChartOrder] = useState([]);
  let [oldChartOrder, setOldChartOrder] = useState([]);
  let [ddlList, setDdlList] = useState(new Map([
    ['Last',1],['%Short',2],['PredictTrend',3],['Peak',4],['Shareholder',5],['manager',6]
  ]));
  let [chartOrder, setChartOrder] = useState(new Map([
    ['Last',1],['%Short',2],['PredictTrend',3],['Peak',4],['Shareholder',5],['manager',6]
  ]));
  let newChartOrder = undefined;

  const locations    = useLocation();
  const searchParams = new URLSearchParams(locations.search);
  const [selectedCategory] = useState('');
  const categoryMap = {
    'พลังงาน': ['BANPU','BGRIM','EGCO','GPSC','GULF','OR','PTT','PTTEP','PTTGC','RATCH','TOP','IVL'],
    'การเงิน':  ['BBL','KBANK','KTB','SCB','TISCO','TTB','KTC','SAWAD','MTC','TLI'],
    'เทคโนโลยี':['ADVANC','DELTA','COM7','CCET','TRUE'],
    'ค้าปลีก':  ['CPALL','CPF','CBG','OSP','GLOBAL','HMPRO','BJC','CRC','ITC','TU'],
    'บริการ':   ['AOT','AWC','BDMS','BH','BEM','BTS','CPN','LH','MINT','SCGP','SCC','VGI','WHA'],
  };
  const filteredSymbols = selectedCategory ? categoryMap[selectedCategory] : Object.values(categoryMap).flat();

  const userDetail   = useSelector(s => s.userDetail);
  const base_api_url = process.env.NODE_ENV === "production"
    ? (window.location.protocol === "https:" ? process.env.REACT_APP_API_HTTPS_URL : process.env.REACT_APP_API_HTTP_URL)
    : "/application";

  const searchInputRef = useRef(null);
  const resChatBoxRef  = useRef(null);

  useEffect(() => {
    resChatBoxRef.current && (resChatBoxRef.current.scrollTop = resChatBoxRef.current.scrollHeight);
  }, [isMessageSent]);

  useEffect(() => {
    const init = async () => {
      const res = await __fetching_data__({ url:`${base_api_url}/chartorder/load`, method:"POST" });
      if (res?.data) {
        newChartOrder = new Map();
        res.data.forEach((k,i) => newChartOrder.set(k, i+1));
        chartOrder = new Map(newChartOrder);
        setChartOrder(chartOrder);
      }
      await generateEmptyChart();
      setTimeout(() => setIsInitialLoading(false), 1500);
    };
    init();
  }, []);

  // ── Helpers ───────────────────────────────────────────────────────────────
  function generateData(data=[], date_column='time', field='value') {
    const tz = 7*3600*1000;
    return data.map(d => ({ time:(new Date(d[date_column]).getTime()+tz)/1000, value:parseFloat(d[field])||0 }));
  }
  function generateGuid() {
    return Math.random().toString(36).slice(2,15) + Math.random().toString(36).slice(2,15);
  }

  const generateEmptyChart = async () => {
    const emptyData = {
      all_59:[],all_246:[],bidask:[],flow_accum:[],high:[],low:[],
      name_59:['date'],name_246:['date'],open:[],outstanding_short:[],
      point_data:[],price:[],s50_scoring:[],short:[],swap:[],
      max_peaks:{swap:{max:[],min:[]}},symbol:'',
    };
    const waitFor = (id,r=20) => new Promise(res => {
      const el=document.getElementById(id);
      if(el) return res(el);
      if(!r)  return res(null);
      setTimeout(()=>waitFor(id,r-1).then(res),100);
    });
    const el = await waitFor('chatboxEmpty');
    if(!el) return;
    el.innerHTML='';
    el.appendChild(await displayPlot({plot_data:emptyData},[],[],true));
  };

  // ── rebuildMultiLineSeries ────────────────────────────────────────────────
  function rebuildMultiLineSeries(chart, key, dataArrays, names) {
    const C=['#FF3B30','#FF9500','#FFCC00','#34C759','#32D74B','#5AC8FA','#007AFF','#AF52DE','#BF5AF2','#FF2D55','#FF6B6B','#FF8200','#FF5E3A','#FFB300','#66D4CF','#FFC300','#50C878','#5856D6','#8E44AD','#1ABC9C'];
    (liveCharts[key]?._seriesArray??[]).flat().forEach(s=>{try{chart.removeSeries(s);}catch(_){}});
    const newArr=[], newActive=[];
    names.slice(1).forEach((name,i)=>{
      const color=C[i%C.length], sd=generateData(dataArrays[i]??[],'time','value');
      const swt=chart.addLineSeries({color}); swt.applyOptions({priceFormat:{type:'volume'},visible:true,crosshairMarkerVisible:false}); swt.setData(sd);
      const st=chart.addLineSeries({title:name,color}); st.applyOptions({priceFormat:{type:'volume'},visible:false}); st.setData(sd);
      newArr.push([swt,st]); newActive.push(swt);
    });
    if(liveCharts[key]){ liveCharts[key].seriesRef=newActive; liveCharts[key]._seriesArray=newArr; }
  }

  // ── applyTheme ────────────────────────────────────────────────────────────
  const applyTheme = useCallback((chart, text) => {
    chart.applyOptions({
      watermark:{ visible:true, fontSize:24, horzAlign:'center', vertAlign:'center', color:theme.palette.chart?.watermark??'rgba(255,255,255,0.15)', text },
      layout:{ attributionLogo:false, background:{type:'solid',color:theme.palette.background.default}, textColor:theme.palette.text.primary },
      grid:{ horzLines:{color:theme.palette.divider}, vertLines:{color:theme.palette.divider} },
    });
  }, [theme]);

  // ── syncAllCharts — wire crosshair + range sync across every live chart ───
  function syncAllCharts() {
    const entries = Object.values(liveCharts).filter(e=>e?.chart);
    if(entries.length < 2) return;
    const charts    = entries.map(e=>e.chart);
    const seriesArr = entries.map(e=>e.seriesRef);
    let syncingRange = false;

    charts.forEach((cur, ci) => {
      // Time-range sync
      cur.timeScale().subscribeVisibleLogicalRangeChange(range => {
        if(syncingRange||!range) return;
        syncingRange = true;
        charts.forEach((o,oi) => { if(oi!==ci) o.timeScale().setVisibleLogicalRange(range); });
        syncingRange = false;
      });

      // Crosshair sync
      cur.subscribeCrosshairMove(param => {
        const mySeries = seriesArr[ci];
        if(!mySeries?.length) return;
        const dp = param.time ? (param.seriesData.get(mySeries[0])??null) : null;
        charts.forEach((other, oi) => {
          if(oi===ci) return;
          if(!param.time||!param.point||param.point.x<0||param.point.y<0) {
            other.clearCrosshairPosition(); return;
          }
          const os = seriesArr[oi];
          if(!os?.length||!dp) return;
          // Find first visible series in target chart
          const ts = os.find(s=>{ try{return s.options().visible;}catch(_){return false;} }) ?? os[0];
          if(ts) try { other.setCrosshairPosition(dp.value, dp.time, ts); } catch(_){}
        });
      });
    });
  }

  // ── triggerChartRefresh ───────────────────────────────────────────────────
  function triggerChartRefresh(chartKey, symbol) {
    const entry = liveCharts[chartKey];
    if(!entry) return;
    const {chart, seriesRef} = entry;
    const pd = liveData;
    if(!pd) return;

    switch(chartKey) {
      case 'Last': {
        const [s] = seriesRef??[];
        if(!s||!pd.price?.length) break;
        applyTheme(chart, symbol);
        s.applyOptions({visible:true});
        s.setData(generateData(pd.price,'time','value'));
        chart.timeScale().fitContent();
        if(pd._corporateEvents && entry.renderMarkers) entry.renderMarkers(pd._corporateEvents);
        chart.timeScale().applyOptions({rightOffset:5});
        break;
      }
      case '%Short': {
        const [s0,s1] = seriesRef??[];
        if(!s0||!pd.short?.length) break;
        applyTheme(chart, symbol);
        chart.priceScale('left').applyOptions({visible:true,autoScale:true,borderVisible:true,scaleMargins:{top:.1,bottom:.1}});
        s0.applyOptions({visible:true});
        s0.setData(generateData(pd.short,'time','value'));
        if(s1&&pd.outstanding_short?.length) {
          s1.applyOptions({visible:true,priceFormat:{type:'volume'}});
          s1.setData(generateData(pd.outstanding_short,'time','value'));
        }
        chart.timeScale().fitContent();
        chart.timeScale().applyOptions({rightOffset:5});
        break;
      }
      case 'PredictTrend': {
        const [s] = seriesRef??[];
        if(!s||!pd.flow_accum?.length) break;
        applyTheme(chart, symbol);
        chart.priceScale('right').applyOptions({visible:true,textColor:theme.palette.text.primary});
        s.applyOptions({visible:true,priceFormat:{type:'volume'}});
        s.setData(generateData(pd.flow_accum,'time','value'));
        chart.timeScale().fitContent();
        chart.timeScale().applyOptions({rightOffset:5});
        break;
      }
      case 'Peak': {
        const [s] = seriesRef??[];
        if(!s||!pd.swap?.length) break;
        applyTheme(chart, symbol);
        chart.priceScale('right').applyOptions({visible:true,textColor:theme.palette.text.primary});
        s.applyOptions({visible:true});
        const minLen = Math.min(pd.price?.length??0, pd.swap.length);
        const sorted = generateData(pd.swap.slice(0,minLen),'time','value').sort((a,b)=>a.time-b.time);
        s.setData(sorted);
        chart.timeScale().fitContent();
        chart.timeScale().applyOptions({rightOffset:5});
        break;
      }
      case 'Shareholder': {
        if(!pd.all_246?.length) break;
        applyTheme(chart, symbol);
        rebuildMultiLineSeries(chart,'Shareholder',pd.all_246,pd.name_246);
        setTimeout(()=>{ chart.timeScale().fitContent(); chart.timeScale().applyOptions({rightOffset:5}); },50);
        break;
      }
      case 'manager': {
        if(!pd.all_59?.length) break;
        applyTheme(chart, symbol);
        rebuildMultiLineSeries(chart,'manager',pd.all_59,pd.name_59);
        setTimeout(()=>{ chart.timeScale().fitContent(); chart.timeScale().applyOptions({rightOffset:5}); },50);
        break;
      }
      default: break;
    }
  }

  // ── handleSendMessage ─────────────────────────────────────────────────────
  const handleSendMessage = async (symbolOverride) => {
    const sym = (symbolOverride||inputValue).trim().toUpperCase();
    if(!sym) return;

    setIsMessageSent(true);
    setInputValue('');
    setMessages(p=>[...p,{text:`You: ${sym}`,isBot:true}]);

    // Mark ALL charts as loading → skeleton overlays appear
    markLoading(CHART_KEYS, true);

    liveCharts={}; liveSymbol=sym;
    liveData = {
      symbol:sym, price:[],open:[],high:[],low:[],
      flow_accum:[],bidask:[],swap:[],short:[],outstanding_short:[],
      all_59:[],name_59:['date'],all_246:[],name_246:['date'],mkbt:[],
      point_data:[],s50_scoring:[],max_peaks:{swap:{max:[],min:[]}},_corporateEvents:[],
    };

    document.getElementById('chatboxEmpty') && (document.getElementById('chatboxEmpty').innerHTML='');
    const chatbox = document.getElementById('chatbox');
    if(!chatbox) return;
    chatbox.innerHTML=''; chatbox.style.display='block';
    setIsSymbolSelected(true);

    // Render empty panels immediately (skeleton overlays cover them via React)
    const responseContainer = await displayPlot({plot_data:liveData},[],[],false,sym);
    document.getElementById('chart_response')?.remove();
    chatbox.appendChild(responseContainer);
    globalResponse = {data:{plot_data:liveData},bdata:[]};

    let syncDone = false;
    const maybeSyncAll = () => {
      if(syncDone) return;
      const ready = Object.values(liveCharts).filter(e=>e?.seriesRef?.length>0).length;
      if(ready>=2){ syncDone=true; syncAllCharts(); }
    };

    // Each endpoint removes its own skeleton when it finishes
    const fetchAndPatch = async (url, affectedKeys, patchFn) => {
      try {
        const res = await __fetching_data__({url,method:'GET'});
        if(res?.data){
          patchFn(res.data);
          affectedKeys.forEach(k=>triggerChartRefresh(k,sym));
          maybeSyncAll();
        }
      } catch(e) {
        console.warn('[progressive] failed:', url, e);
      } finally {
        // Remove skeleton for this chart's keys
        markLoading(affectedKeys, false);
      }
    };

    await Promise.all([
      fetchAndPatch(`${base_api_url}/chat_bot_six_request?symbol=${sym}&chart=price`, ['Last'], d=>{
        liveData.price=d.price??[]; liveData.open=d.open??[]; liveData.high=d.high??[]; liveData.low=d.low??[]; liveData.s50_ind=d.s50_ind??null;
      }),
      fetchAndPatch(`${base_api_url}/chat_bot_six_request?symbol=${sym}&chart=flow`, ['%Short'], d=>{
        liveData.flow_accum=d.flow_accum??d.flow??[];
      }),
      fetchAndPatch(`${base_api_url}/chat_bot_six_request?symbol=${sym}&chart=bidask`, [], d=>{
        liveData.bidask=d.bidask??[];
      }),
      fetchAndPatch(`${base_api_url}/chat_bot_six_request?symbol=${sym}&chart=swap`, ['Peak'], d=>{
        liveData.swap=d.swap??[];
      }),
      fetchAndPatch(`${base_api_url}/chat_bot_six_request?symbol=${sym}&chart=flow`, ['PredictTrend'], d=>{
        liveData.short=d.short??[]; liveData.outstanding_short=d.outstanding_short??[];
      }),
      fetchAndPatch(`${base_api_url}/chat_bot_six_request?symbol=${sym}&chart=report`, ['Shareholder','manager'], d=>{
        liveData.all_59=d.all_59??[]; liveData.name_59=d.name_59??['date'];
        liveData.all_246=d.all_246??[]; liveData.name_246=d.name_246??['date'];
        liveData.mkbt=d.mkbt??[];
      }),
      fetchAndPatch(`${base_api_url}/api/symbols/${sym}`, ['Last'], d=>{
        liveData._corporateEvents=d;
      }),
    ]);

    setIsMessageSent(false);
    if(!syncDone) syncAllCharts();
    // Ensure all skeletons off
    markLoading(CHART_KEYS, false);
  };

  // ── displayPlot ──────────────────────────────────────────────────────────
  const displayPlot = async (response, bresponse, corporateEvents, isEmpty=false, symbol='') => {
    const plotData = response.plot_data ?? response;
    window.plotData = plotData;

    const rc = document.createElement('div');
    rc.id='chart_response';
    rc.style.cssText=`display:grid;padding:0;justify-items:center;width:${isMobile?'180%':'100%'};grid-template-columns:repeat(2,1fr);gap:8px;background:#0B0E14;`;

    const chartOption = {
      crosshair:{mode:0}, timeScale:{visible:true},
      layout:{background:{type:'solid',color:'#131722'},textColor:'#787B86'},
      grid:{vertLines:{color:'#2B3139'},horzLines:{color:'#2B3139'}},
    };

    const emptyWatermark = { watermark:{visible:true,fontSize:22,horzAlign:'center',vertAlign:'center',color:'#D1D4DC',text:'Please select symbol',fontFamily:'Roboto'}, layout:{attributionLogo:false,background:{type:'solid',color:'#131722'},textColor:'#787B86'}, grid:{horzLines:{color:'#2B3139'},vertLines:{color:'#2B3139'}} };

    function addDummy(chart) {
      const s=chart.addLineSeries({color:'transparent',crosshairMarkerVisible:false,priceLineVisible:false});
      const base=Math.floor(Date.now()/1000)-120*86400;
      s.setData([...Array(11)].map((_,i)=>({time:base+i*12*86400,value:i/10})));
      chart.timeScale().fitContent();
    }

    const isDataEmpty = !plotData?.price||plotData.price.length<=1;
    const data2 = (()=>{
      const p=plotData.price||[], s=plotData.swap||[];
      const min=Math.min(p.length,s.length);
      return s.slice(0,min);
    })();

    function getByValue(v){ for(const[k,val]of chartOrder.entries()) if(val===v) return k; }

    function createOrderSel(wrap, idxSel, valueKey) {
      const sel=document.createElement('select'); sel.id=idxSel;
      let newCO=new Map();
      if(ddlChartOrder.indexOf(valueKey)!==-1){ newCO=new Map(JSON.parse(JSON.stringify(Array.from(ddlList)))); }
      else{ for(const[k,v]of ddlList.entries()) if(ddlChartOrder.indexOf(k)===-1) newCO.set(k,v); }
      for(const[k,v]of new Map(newCO)){
        const opt=document.createElement('option'); opt.value=v; opt.textContent=k;
        if(k===valueKey) opt.selected=true;
        sel.onchange=async e=>{
          setLoading(true);
          document.getElementById('chart_response')?.remove();
          const ni=parseInt(e.target.value), ol=getByValue(idxSel), nl=getByValue(ni);
          chartOrder.set(ol,ni); chartOrder.set(nl,idxSel);
          ddlList.set(ol,ni);   ddlList.set(nl,idxSel);
          setDdlList(ddlList); setChartOrder(chartOrder);
          oldChartOrder.push(ol); ddlChartOrder.push(nl);
          setOldChartOrder(oldChartOrder); setDDLChartOrder(ddlChartOrder);
          const cb=document.getElementById('chatbox');
          if(globalResponse){ const s2=globalResponse.data?.plot_data?.symbol||symbol; cb.appendChild(await displayPlot(globalResponse.data,globalResponse.bdata,[],false,s2)); }
          else generateEmptyChart();
          setLoading(false);
        };
        sel.appendChild(opt);
      }
      sel.className='header-selection'; wrap.appendChild(sel);
    }

    // ── Chart factories ───────────────────────────────────────────────────

    function mkWrap() {
      const w=document.createElement('div');
      w.style.cssText=`position:relative;border-radius:8px;overflow:hidden;border:1px solid #2B3139;width:100%;height:${chartHeight}px;`;
      return w;
    }
    function mkResizeObs(chart,wrap){ new ResizeObserver(es=>es.forEach(e=>requestAnimationFrame(()=>chart.applyOptions({width:e.contentRect.width,height:e.contentRect.height})))).observe(wrap); }

    // Last
    function genLast() {
      const wrap=mkWrap(); wrap.id='chart-1';
      const ttip=document.createElement('div');
      Object.assign(ttip.style,{position:'fixed',display:'none',background:'white',color:'#000',border:'1px solid #ccc',padding:'8px',boxShadow:'0 2px 6px rgba(0,0,0,.15)',fontSize:'12px',maxWidth:'200px',pointerEvents:'none',zIndex:'9999'});
      document.body.appendChild(ttip);

      const chart=createChart(wrap,{...chartOption,width:wrap.offsetWidth,height:wrap.offsetHeight});
      chart.applyOptions({rightPriceScale:{visible:true},leftPriceScale:{visible:true,mode:PriceScaleMode.Normal,autoScale:true}});
      mkResizeObs(chart,wrap); createOrderSel(wrap,1,'Last');

      const tipEl=document.createElement('div');
      Object.assign(tipEl.style,{position:'absolute',display:'none',pointerEvents:'none',background:'white',border:'1px solid #ccc',padding:'8px',fontSize:'12px',maxWidth:'200px',zIndex:'10001'});
      wrap.appendChild(tipEl);

      const renderMarkers=(evts=[])=>{
        wrap.querySelectorAll('.marker-pane,.event-marker').forEach(n=>n.remove());
        tipEl.style.display='none';
        if(!Array.isArray(evts)) return;
        const pw=chart.priceScale('right').width();
        const pane=document.createElement('div');
        Object.assign(pane.style,{position:'absolute',left:'57px',right:`${pw}px`,bottom:'30px',height:'16px',overflow:'hidden',pointerEvents:'none'});
        pane.className='marker-pane'; wrap.appendChild(pane);
        const tz=7*3600*1000;
        evts.forEach(evt=>{
          const t=(new Date(evt.date).getTime()+tz)/1000, x=chart.timeScale().timeToCoordinate(t);
          if(x==null) return;
          const bg=evt.appoint.length&&evt.resign.length?'conic-gradient(#4caf50 0deg 180deg,#f44336 180deg 360deg)':(evt.appoint.length?'#4caf50':'#f44336');
          const m=document.createElement('div');
          Object.assign(m.style,{position:'absolute',left:`${x-8}px`,bottom:'0',width:'16px',height:'16px',lineHeight:'16px',textAlign:'center',fontSize:'10px',borderRadius:'50%',background:bg,color:'white',cursor:'pointer',pointerEvents:'auto',zIndex:'10002'});
          m.className='event-marker'; m.textContent='C';
          m.addEventListener('click',ev=>{
            let h=`<div style="font-weight:bold">${evt.date}</div>`;
            if(evt.appoint.length) h+=`<b>Appoint:</b><ul>${evt.appoint.map(n=>`<li>${n}</li>`).join('')}</ul>`;
            if(evt.resign.length)  h+=`<b>Resign:</b><ul>${evt.resign.map(n=>`<li>${n}</li>`).join('')}</ul>`;
            ttip.innerHTML=h; ttip.style.left=(ev.pageX+10)+'px'; ttip.style.top=(ev.pageY-40)+'px'; ttip.style.display='block';
          });
          pane.appendChild(m);
        });
      };

      let activeSeries=[];
      if(isDataEmpty){
        chart.applyOptions(emptyWatermark); addDummy(chart);
        const s=chart.addLineSeries({color:'#00A5E3',lastValueVisible:true,priceLineVisible:false,priceScaleId:'right'});
        s.applyOptions({visible:false}); activeSeries=[s];
        chart.timeScale().subscribeVisibleTimeRangeChange(()=>{ ttip.style.display='none'; if(liveData?._corporateEvents) renderMarkers(liveData._corporateEvents); });
      } else {
        applyTheme(chart,symbol);
        const s=chart.addLineSeries({color:'#00A5E3',lastValueVisible:true,priceLineVisible:false,priceScaleId:'right'});
        s.setData(generateData(plotData.price,'time','value')); activeSeries=[s];
        chart.timeScale().subscribeVisibleTimeRangeChange(()=>{ ttip.style.display='none'; renderMarkers(corporateEvents); });
        renderMarkers(corporateEvents);
        setTimeout(()=>chart.timeScale().applyOptions({rightOffset:5}),100);
      }
      liveCharts['Last']={chart,seriesRef:activeSeries,containerEl:wrap,renderMarkers};
      if(!window.myCharts) window.myCharts={}; window.myCharts['chart']=chart;
      return [chart,activeSeries,wrap];
    }

    // %Short
    function genShort() {
      const wrap=mkWrap();
      const chart=createChart(wrap,{...chartOption,leftPriceScale:{visible:false},width:wrap.offsetWidth,height:wrap.offsetHeight});
      mkResizeObs(chart,wrap); createOrderSel(wrap,2,'%Short');
      let activeSeries=[];
      if(isDataEmpty){
        chart.applyOptions(emptyWatermark); addDummy(chart);
        const s0=chart.addLineSeries({color:'#f72a2a',lastValueVisible:true,title:'% daily short',priceLineVisible:false});
        const s1=chart.addLineSeries({color:'#73f54c',lastValueVisible:true,title:'outstandingshort',priceLineVisible:false,priceScaleId:'left'});
        s0.applyOptions({visible:false}); s1.applyOptions({visible:false}); activeSeries=[s0,s1];
      } else {
        applyTheme(chart,symbol);
        chart.priceScale('left').applyOptions({autoScale:true,visible:true,borderVisible:true,scaleMargins:{top:.1,bottom:.1}});
        const s0=chart.addLineSeries({color:'#f72a2a',lastValueVisible:true,title:'% daily short',priceLineVisible:false});
        const s1=chart.addLineSeries({color:'#73f54c',lastValueVisible:true,title:'outstandingshort',priceLineVisible:false,priceScaleId:'left'});
        s0.setData(generateData(plotData.short,'time','value'));
        s1.setData(generateData(plotData.outstanding_short,'time','value'));
        s1.applyOptions({priceFormat:{type:'volume'}}); activeSeries=[s0,s1];
        setTimeout(()=>chart.timeScale().applyOptions({rightOffset:5}),100);
      }
      liveCharts['%Short']={chart,seriesRef:activeSeries,containerEl:wrap};
      return [chart,activeSeries,wrap];
    }

    // PredictTrend
    function genFlow() {
      const wrap=mkWrap();
      const chart=createChart(wrap,{...chartOption,width:wrap.offsetWidth,height:wrap.offsetHeight});
      chart.priceScale('right').applyOptions({visible:false,textColor:'transparent'});
      chart.priceScale('left').applyOptions({visible:false});
      mkResizeObs(chart,wrap); createOrderSel(wrap,3,'PredictTrend');
      let activeSeries=[];
      if(isDataEmpty){
        chart.applyOptions(emptyWatermark);
        chart.priceScale('right').applyOptions({visible:true,textColor:'#787B86'});
        addDummy(chart);
        const s=chart.addLineSeries({color:'#ffa34d',lineStyle:0,lineWidth:3,lastValueVisible:false,priceLineVisible:false});
        s.applyOptions({visible:false}); activeSeries=[s];
      } else {
        applyTheme(chart,symbol);
        const s=chart.addLineSeries({color:'#ffa34d',lineStyle:0,lineWidth:3,lastValueVisible:false,priceLineVisible:false});
        s.setData(generateData(plotData.flow_accum,'time','value')); s.applyOptions({priceFormat:{type:'volume'}}); activeSeries=[s];
        setTimeout(()=>chart.timeScale().applyOptions({rightOffset:5}),100);
      }
      liveCharts['PredictTrend']={chart,seriesRef:activeSeries,containerEl:wrap};
      return [chart,activeSeries,wrap];
    }

    // Peak
    function genPeak() {
      const wrap=mkWrap();
      const chart=createChart(wrap,{...chartOption,width:wrap.offsetWidth,height:wrap.offsetHeight});
      chart.priceScale('right').applyOptions({visible:false,textColor:'transparent'});
      chart.priceScale('left').applyOptions({visible:false});
      mkResizeObs(chart,wrap); createOrderSel(wrap,4,'Peak');
      let activeSeries=[];
      if(isDataEmpty){
        chart.applyOptions(emptyWatermark);
        chart.priceScale('right').applyOptions({visible:true,textColor:'#787B86'});
        addDummy(chart);
        const s=chart.addLineSeries({color:'#faed37',lineStyle:0,lineWidth:2,title:'sum',lastValueVisible:false,priceLineVisible:false,priceScaleId:'right'});
        s.applyOptions({visible:false}); activeSeries=[s];
      } else {
        applyTheme(chart,symbol);
        const s=chart.addLineSeries({color:'#faed37',lineStyle:0,lineWidth:2,title:'sum',lastValueVisible:false,priceLineVisible:false,priceScaleId:'right'});
        const sorted=generateData(data2,'time','value').sort((a,b)=>a.time-b.time); s.setData(sorted); activeSeries=[s];
        let markers=[], max_data=plotData.max_peaks, fv=plotData.flow_accum.map(i=>i.value);
        let gid='',ctrl=false;
        window.addEventListener('keydown',e=>{if(e.ctrlKey){ctrl=true;if(!gid)gid=generateGuid();}});
        window.addEventListener('keyup',()=>{ctrl=false;gid='';});
        chart.subscribeDblClick(param=>{
          const maxP=max_data.swap.max, minP=max_data.swap.min; let b=false;
          for(let i=0;i<markers.length;i++){
            if(param.time==markers[i].time){ b=true; markers.splice(i,1); let v=''; for(const[,val]of param.seriesData)v=val.value;
              __fetching_data__({url:`${base_api_url}/chat_bot_save_point`,method:"POST",body:JSON.stringify({chart_name:"peak",value:v,method:"del",timestamp:param.time})}); }
          }
          if(!b){
            let bx=false,bn=false,vs='',vp='',bp='';
            for(const[,val]of param.seriesData){
              if(maxP.includes(val.value)){vs=val.value;vp='A';bx=true;bp='aboveBar';}
              else if(minP.includes(val.value)){vs=val.value;vp='V';bn=true;bp='belowBar';}
            }
            let cc='',ch='';
            if(ctrl){if(bx){cc='#00FF00';ch='U';}else if(bn){cc='#7b241c';ch='D';}}
            else{if(bx){cc='#008000';ch='U';}else if(bn){cc='#FF0000';ch='D';}}
            markers=[...markers,{time:param.time,position:bp,color:cc,shape:'circle',text:ch}];
            __fetching_data__({url:`${base_api_url}/chat_bot_save_point`,method:"POST",body:JSON.stringify({chart_name:"peak",value:vs,position:vp,last_value:fv[fv.length-1],method:"add",symbol:plotData.symbol,timestamp:param.time,group_id:gid})});
          }
          markers.sort((a,b)=>a.time-b.time); s.setMarkers(markers);
        });
        setTimeout(()=>chart.timeScale().applyOptions({rightOffset:5}),100);
      }
      liveCharts['Peak']={chart,seriesRef:activeSeries,containerEl:wrap};
      return [chart,activeSeries,wrap];
    }

    // Shareholder
    function gen246() {
      const wrap=mkWrap();
      const chart=createChart(wrap,{...chartOption,width:wrap.offsetWidth,height:wrap.offsetHeight});
      mkResizeObs(chart,wrap); createOrderSel(wrap,5,'Shareholder');
      const sarr=[]; let activeSeries=[];
      if(isDataEmpty){ chart.applyOptions(emptyWatermark); addDummy(chart); }
      else {
        applyTheme(chart,symbol);
        const C=['#FF3B30','#FF9500','#FFCC00','#34C759','#32D74B','#5AC8FA','#007AFF','#AF52DE','#BF5AF2','#FF2D55','#FF6B6B','#FF8200','#FF5E3A','#FFB300','#66D4CF','#FFC300','#50C878','#5856D6','#8E44AD','#1ABC9C'];
        plotData.name_246.slice(1).forEach((name,i)=>{
          const color=C[i%C.length],sd=generateData(plotData.all_246[i],'time','value');
          const swt=chart.addLineSeries({color}); swt.applyOptions({priceFormat:{type:'volume'},visible:true,crosshairMarkerVisible:false}); swt.setData(sd);
          const st=chart.addLineSeries({title:name,color}); st.applyOptions({priceFormat:{type:'volume'},visible:false}); st.setData(sd);
          sarr.push([swt,st]); activeSeries.push(swt);
        });
        let tv=false;
        const btn=document.createElement('button'); btn.textContent='Hide/Show All';
        Object.assign(btn.style,{position:'absolute',top:'50px',left:'12px',zIndex:'100',background:'#2B3139',color:'#D1D4DC',border:'1px solid #363C4E',borderRadius:'4px',padding:'4px 8px',cursor:'pointer',fontSize:'12px'});
        btn.addEventListener('click',()=>{ tv=!tv; sarr.forEach(([s,t])=>{ if(tv){s.applyOptions({visible:false});t.applyOptions({visible:true});}else{s.applyOptions({visible:true});t.applyOptions({visible:false});} }); }); wrap.appendChild(btn);
        chart.subscribeClick(p=>{ if(!p?.point||!p.hoveredSeries) return; const idx=sarr.findIndex(s=>s.includes(p.hoveredSeries)); if(idx===-1) return; const[swt,st]=sarr[idx]; if(st.options().visible){swt.applyOptions({visible:true});st.applyOptions({visible:false});}else{swt.applyOptions({visible:false});st.applyOptions({visible:true});} });
        setTimeout(()=>chart.timeScale().applyOptions({rightOffset:5}),100);
      }
      liveCharts['Shareholder']={chart,seriesRef:activeSeries,_seriesArray:sarr,containerEl:wrap};
      return [chart,activeSeries,wrap];
    }

    // Manager
    function gen59() {
      const wrap=mkWrap();
      const chart=createChart(wrap,{...chartOption,width:wrap.offsetWidth,height:wrap.offsetHeight});
      mkResizeObs(chart,wrap); createOrderSel(wrap,6,'manager');
      const sarr=[]; let activeSeries=[];
      if(isDataEmpty){ chart.applyOptions(emptyWatermark); addDummy(chart); }
      else {
        applyTheme(chart,symbol);
        const C=['#FF3B30','#FF9500','#FFCC00','#34C759','#32D74B','#5AC8FA','#007AFF','#AF52DE','#BF5AF2','#FF2D55','#FF6B6B','#FF8200','#FF5E3A','#FFB300','#66D4CF','#FFC300','#50C878','#5856D6','#8E44AD','#1ABC9C'];
        plotData.name_59.slice(1).forEach((name,i)=>{
          const color=C[i%C.length],sd=generateData(plotData.all_59[i],'time','value');
          const swt=chart.addLineSeries({color}); swt.applyOptions({priceFormat:{type:'volume'},visible:true,crosshairMarkerVisible:false}); swt.setData(sd);
          const st=chart.addLineSeries({title:name,color}); st.applyOptions({priceFormat:{type:'volume'},visible:false}); st.setData(sd);
          sarr.push([swt,st]); activeSeries.push(swt);
        });
        let tv=false;
        const btn=document.createElement('button'); btn.textContent='Show/Hide All';
        Object.assign(btn.style,{position:'absolute',top:'50px',left:'12px',zIndex:'100',background:'#2B3139',color:'#D1D4DC',border:'1px solid #363C4E',borderRadius:'4px',padding:'4px 8px',cursor:'pointer',fontSize:'12px'});
        btn.addEventListener('click',()=>{ tv=!tv; sarr.forEach(([s,t])=>{ if(tv){s.applyOptions({visible:false});t.applyOptions({visible:true});}else{s.applyOptions({visible:true});t.applyOptions({visible:false});} }); }); wrap.appendChild(btn);
        chart.subscribeClick(p=>{ if(!p?.point||!p.hoveredSeries) return; sarr.forEach(([s])=>s.applyOptions({lastValueVisible:false,priceLineVisible:false})); const idx=sarr.findIndex(s=>s.includes(p.hoveredSeries)); if(idx===-1) return; const[swt,st]=sarr[idx]; swt.applyOptions({lastValueVisible:true,priceLineVisible:true}); if(st.options().visible){swt.applyOptions({visible:true});st.applyOptions({visible:false});sarr.forEach(([s])=>s.applyOptions({lastValueVisible:true,priceLineVisible:true}));}else{swt.applyOptions({visible:false});st.applyOptions({visible:true});} });
        setTimeout(()=>chart.timeScale().applyOptions({rightOffset:5}),100);
      }
      liveCharts['manager']={chart,seriesRef:activeSeries,_seriesArray:sarr,containerEl:wrap};
      return [chart,activeSeries,wrap];
    }

    // ── Build all ─────────────────────────────────────────────────────────
    const [c1,s1,w1]=genLast(), [c8,s8,w8]=genShort(), [c4,s4,w4]=genFlow();
    const [c2,s2,w2]=genPeak(), [c6,s6,w6]=gen246(),   [c5,s5,w5]=gen59();

    let allW,allS,charts;
    if(userDetail.permissions?.permission?.chatBot9Chanel?.read){
      allW=[w1,w8,w4,w2,w6,w5]; allS=[s1,s8,s4,s2,s6,s5]; charts=[c1,c8,c4,c2,c6,c5];
      if(!window.myCharts) window.myCharts={}; window.myCharts['chart']=c1;
      for(const[key,val]of chartOrder){ const i=val-1;
        if(key==='Last')          {allW[i]=w1;allS[i]=s1;charts[i]=c1;}
        else if(key==='%Short')   {allW[i]=w8;allS[i]=s8;charts[i]=c8;}
        else if(key==='PredictTrend'){allW[i]=w4;allS[i]=s4;charts[i]=c4;}
        else if(key==='Peak')     {allW[i]=w2;allS[i]=s2;charts[i]=c2;}
        else if(key==='Shareholder'){allW[i]=w6;allS[i]=s6;charts[i]=c6;}
        else if(key==='manager')  {allW[i]=w5;allS[i]=s5;charts[i]=c5;}
      }
    } else {
      allW=[w1,w4,w5,w6,w8]; allS=[s1,s4,s5,s6,s8]; charts=[c1,c4,c5,c6,c8];
    }

    // Sync time range for re-render (chart order change)
    if(!isDataEmpty){
      let syncing=false;
      charts.forEach((cur,ci)=>{
        cur.timeScale().subscribeVisibleLogicalRangeChange(range=>{
          if(syncing||!range) return; syncing=true;
          charts.forEach((o,oi)=>{ if(oi!==ci) o.timeScale().setVisibleLogicalRange(range); });
          syncing=false;
        });
        cur.subscribeCrosshairMove(param=>{
          const ms=allS[ci]; if(!ms?.length) return;
          const dp=param.time?(param.seriesData.get(ms[0])??null):null;
          charts.forEach((other,oi)=>{
            if(oi===ci) return;
            if(!param.time||!param.point||param.point.x<0||param.point.y<0){other.clearCrosshairPosition();return;}
            const os=allS[oi]; if(!os?.length||!dp) return;
            const ts=os.find(s=>{try{return s.options().visible;}catch(_){return false;}})?? os[0];
            if(ts) try{other.setCrosshairPosition(dp.value,dp.time,ts);}catch(_){}
          });
        });
      });
    }

    allW.forEach(w=>rc.appendChild(w));
    return rc;
  };

  function saveChartOrder() {
    setLoading(true);
    const sorted=new Map([...chartOrder.entries()].sort((a,b)=>a[1]-b[1]));
    const params={}; let n=1; for(const[k]of sorted){params[`d${n}`]=k;n++;}
    __fetching_data__({url:`${base_api_url}/chartorder/save`,method:"POST",body:JSON.stringify(params)})
      .then(()=>showToast("บันทึกรูปแบบกราฟเรียบร้อยแล้ว 💾","success"))
      .finally(()=>setLoading(false));
  }
  function goToLastData(){ window.myCharts?.['chart']?.timeScale().scrollToPosition(7,false); }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        .header-selection{position:absolute;top:12px;left:12px;z-index:100;background:#2B3139;color:#D1D4DC;border:1px solid #363C4E;border-radius:4px;padding:4px 8px;font-family:Roboto,sans-serif;font-size:12px;cursor:pointer;outline:none;}
        .header-selection:hover{border-color:#4C525E;}
        @keyframes cbi-spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
        .cbi-spin-icon{animation:cbi-spin 0.5s linear;}
      `}</style>

      <Container style={{marginTop:-5,width:'100%',height:isMobile?'90vh':'calc(100vh - var(--top-bar-height))',display:'flex',flexDirection:'column',padding:0,maxWidth:'25000px',background:'#0B0E14'}}>
        <Paper elevation={3} style={{padding:10,flexGrow:1,display:'flex',flexDirection:'column',height:'100%',maxWidth:'25000px',background:'transparent',justifyContent:'flex-start',overflow:'hidden'}}>

          {/* TOP BAR */}
          <div style={{width:'100%',marginBottom:'10px',display:'flex',alignItems:'center',gap:'10px'}}>
            <div style={{position:'relative',width:isMobile?'100%':'45%'}}>
              <Autocomplete
                freeSolo options={filteredSymbols} inputValue={inputValue}
                onInputChange={(_,v)=>setInputValue(v)}
                onChange={(_,v)=>{ if(v){setInputValue(v);handleSendMessage(v);} }}
                sx={{width:'100%'}}
                renderInput={params=>(
                  <TextField {...params} inputRef={searchInputRef} placeholder="Type a Symbol..." variant="outlined" size="small"
                    InputProps={{...params.InputProps,
                      sx:{backgroundColor:'transparent',border:'1px solid rgba(255,255,255,0.3)',borderRadius:'30px',color:'white',fontSize:'14px',height:'40px','& fieldset':{border:'none'}},
                      endAdornment:inputValue?<IconButton size="small" onClick={()=>setInputValue('')} sx={{color:'rgba(255,255,255,.5)'}}><CloseIcon fontSize="small"/></IconButton>:null,
                    }}
                  />
                )}
                disabled={isMessageSent}
                componentsProps={{paper:{sx:{backgroundColor:'#0f172a',border:'1px solid #334155',borderRadius:'12px','& .MuiAutocomplete-option':{color:'#cbd5e1',fontSize:'14px'}}}}}
              />
            </div>
            <div style={{display:'flex',gap:'10px'}}>
              <IconButton sx={{color:'white',borderRadius:'10px',border:'1px solid rgba(255,255,255,.3)'}} onClick={saveChartOrder} title="Save chart order"><SaveOutlinedIcon/></IconButton>
              <IconButton sx={{color:'white',borderRadius:'10px',border:'1px solid rgba(255,255,255,.3)'}} onClick={()=>{
                setInputValue(''); globalResponse=null; liveCharts={}; liveData=null; liveSymbol='';
                setIsSymbolSelected(false); setRefreshing(true);
                markLoading(CHART_KEYS,false);
                document.getElementById('chatbox')&&(document.getElementById('chatbox').innerHTML='');
                document.getElementById('chatboxEmpty')&&(document.getElementById('chatboxEmpty').innerHTML='');
                generateEmptyChart();
                setTimeout(()=>{setRefreshing(false);searchInputRef.current?.focus();},500);
              }}><RefreshIcon className={refreshing?'cbi-spin-icon':''}/></IconButton>
              <IconButton sx={{color:'white',borderRadius:'10px',border:'1px solid rgba(255,255,255,.3)'}} onClick={goToLastData} title="Go to latest"><FastForwardIcon/></IconButton>
            </div>
          </div>

          {/* CHART AREA */}
          <div ref={resChatBoxRef} style={{width:'100%',flexGrow:1,position:'relative',overflowY:'auto',overflowX:'hidden'}}>

            {/* 1. Initial full-page skeleton */}
            {isInitialLoading && (
              <div style={{width:'100%',display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:'8px'}}>
                {CHART_KEYS.map((k,i)=><WaveSkeleton key={k} delay={i*0.18}/>)}
              </div>
            )}

            {/* 2. Empty placeholder (before symbol selected) */}
            <div id="chatboxEmpty" style={{width:'100%',display:(!isInitialLoading&&!isSymbolSelected)?'block':'none'}}/>

            {/* 3. Live charts */}
            <div id="chatbox" style={{width:'100%',position:'relative',display:isSymbolSelected?'block':'none'}}>
              {/* Per-chart skeleton overlays — sit on top of the DOM chart grid */}
              {isSymbolSelected && (
                <div style={{
                  display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:'8px',
                  position:'absolute',top:0,left:0,right:0,
                  pointerEvents:'none',zIndex:400,
                }}>
                  {CHART_KEYS.map(k=>(
                    <div key={k} style={{height:chartHeight,position:'relative'}}>
                      {chartLoading[k] && <ChartSkeleton/>}
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>

          <ToastContainer toasts={toasts}/>
        </Paper>
      </Container>
    </>
  );
};

export default ChatBotV2Page;